/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  getDashboardPins as getDashboardPinsAPI,
  addDashboardPin as addDashboardPinAPI,
  updateDashboardPin as updateDashboardPinAPI,
  deleteDashboardPin as deleteDashboardPinAPI,
} from 'DashboardPinAPI';
import { filterUserResourcesToArray } from 'Utils';

export const ADD_DASHBOARD_PIN = 'ADD_DASHBOARD_PIN';
export const ADD_DASHBOARD_PIN_FAILURE = 'ADD_DASHBOARD_PIN_FAILURE';
export const ADD_DASHBOARD_PIN_SUCCESS = 'ADD_DASHBOARD_PIN_SUCCESS';

export const GET_DASHBOARD_PINS = 'GET_DASHBOARD_PINS';
export const GET_DASHBOARD_PINS_FAILURE = 'GET_DASHBOARD_PINS_FAILURE';
export const GET_DASHBOARD_PINS_SUCCESS = 'GET_DASHBOARD_PINS_SUCCESS';

export const DELETE_DASHBOARD_PIN = 'DELETE_DASHBOARD_PIN';
export const DELETE_DASHBOARD_PIN_FAILURE = 'DELETE_DASHBOARD_PIN_FAILURE';
export const DELETE_DASHBOARD_PIN_SUCCESS = 'DELETE_DASHBOARD_PIN_SUCCESS';

export const UPDATE_DASHBOARD_PIN = 'UPDATE_DASHBOARD_PIN';
export const UPDATE_DASHBOARD_PIN_FAILURE = 'UPDATE_DASHBOARD_PIN_FAILURE';
export const UPDATE_DASHBOARD_PIN_SUCCESS = 'UPDATE_DASHBOARD_PIN_SUCCESS';

export const CAPTURING_PIN_THUMBNAIL = 'CAPTURING_PIN_THUMBNAIL';

const getDashboardPins = () => ({
  type: GET_DASHBOARD_PINS,
});

const getDashboardPinsFailure = error => ({
  type: GET_DASHBOARD_PINS_FAILURE,
  payload: error,
});

const getDashboardPinsSuccess = granularities => ({
  type: GET_DASHBOARD_PINS_SUCCESS,
  payload: granularities,
});

export const fetchDashboardPins =
  hierarchyPath =>
  (dispatch, getState, { dataCache }) => {
    dispatch(getDashboardPins());
    return getDashboardPinsAPI(hierarchyPath)
      .then(responseJson => {
        if (responseJson) {
          return filterUserResourcesToArray(
            // add uuid property required for filtering
            responseJson.map(r => ({ ...r, uuid: r?.resourceDto?.uuid || '' })),
            dataCache
          ).map(resource => {
            // remove uuid property to maintain object structure
            const r = resource;
            delete r.uuid;
            return r;
          });
        }
        return null;
      })
      .then(responseJson => {
        dispatch(getDashboardPinsSuccess(responseJson || []));
        return responseJson;
      })
      .catch(error => {
        dispatch(getDashboardPinsFailure(error));
      });
  };

const startAddDashboardPin = pinConfig => ({
  type: ADD_DASHBOARD_PIN,
  payload: pinConfig,
});

const addDashboardPinFailure = error => ({
  type: ADD_DASHBOARD_PIN_FAILURE,
  payload: error,
});

const addDashboardPinSuccess = pin => ({
  type: ADD_DASHBOARD_PIN_SUCCESS,
  payload: pin,
});

export const addDashboardPin =
  (
    hierarchyPath,
    name,
    dashboardMetaId,
    description,
    category,
    summary,
    favorite = false,
    homePage = false,
    filterJson = null,
    thumbnail = null,
    deviceId
  ) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(
      startAddDashboardPin({
        name,
        dashboardMetaId,
        description,
        category,
        summary,
        favorite,
        homePage,
        filterJson,
        thumbnail,
      })
    );
    return addDashboardPinAPI(
      hierarchyPath,
      name,
      dashboardMetaId,
      description,
      category,
      summary,
      favorite,
      homePage,
      Object.keys(filterJson || []).length === 0 ? null : filterJson,
      thumbnail,
      deviceId,
      dataCache.get('currentUser')?.id
    )
      .then(responseJson => {
        dispatch(addDashboardPinSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(addDashboardPinFailure(error));
        throw error;
      });
  };

const startUpdateDashboardPin = () => ({
  type: UPDATE_DASHBOARD_PIN,
});

const updateDashboardPinFailure = error => ({
  type: UPDATE_DASHBOARD_PIN_FAILURE,
  payload: error,
});

const updateDashboardPinSuccess = response => ({
  type: UPDATE_DASHBOARD_PIN_SUCCESS,
  payload: response,
});

export const updateDashboardPin =
  (
    hierarchyPath,
    dashboardPinId,
    name,
    dashboardMetaId,
    description,
    category,
    summary,
    favorite = false,
    homePage = false,
    filterJson = null,
    thumbnail = null,
    deviceId = null
  ) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(startUpdateDashboardPin());
    const userEmailId = dataCache.get('currentUser')?.id;

    return updateDashboardPinAPI(
      hierarchyPath,
      dashboardPinId,
      name,
      dashboardMetaId,
      description,
      category,
      summary,
      favorite,
      homePage,
      Object.keys(filterJson || []).length === 0 ? null : filterJson,
      thumbnail,
      deviceId,
      userEmailId
    )
      .then(responseJson => {
        dispatch(updateDashboardPinSuccess(responseJson || {}));
        return responseJson;
      })
      .catch(error => {
        dispatch(updateDashboardPinFailure(error));
        throw error;
      });
  };

const startDeleteDashboardPin = () => ({
  type: DELETE_DASHBOARD_PIN,
});

const deleteDashboardPinFailure = error => ({
  type: DELETE_DASHBOARD_PIN_FAILURE,
  payload: error,
});

const deleteDashboardPinSuccess = dashboardPinId => ({
  type: DELETE_DASHBOARD_PIN_SUCCESS,
  payload: { dashboardPinId },
});

export const deleteDashboardPin =
  (hierarchyPath, dashboardPinId) =>
  (dispatch, getState__, { dataCache }) => {
    dispatch(startDeleteDashboardPin());
    const userEmailId = dataCache.get('currentUser')?.id;

    return deleteDashboardPinAPI(hierarchyPath, dashboardPinId, userEmailId)
      .then(responseJson => {
        dispatch(deleteDashboardPinSuccess(dashboardPinId));
        return responseJson;
      })
      .catch(error => {
        dispatch(deleteDashboardPinFailure(error));
        throw error;
      });
  };

export const notifyCapturingPinThumbnail = () => dispatch =>
  dispatch({
    type: CAPTURING_PIN_THUMBNAIL,
  });
